import * as React from "react";

import { cn } from "@/lib/cn";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/**
 * SlideDrawer
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.7
 *
 * An opinionated `<Sheet variant="floating" side="right">` with title /
 * description / headerRight slots. Every add/edit form in the app opens in
 * one of these — see §3.7's "no centered modal forms" rule.
 *
 * Sizes:
 *   sm  → sm:max-w-sm
 *   md  → sm:max-w-md   (default — covers 14/19 canonical drawer instances)
 *   lg  → sm:max-w-lg
 *   xl  → sm:max-w-xl
 *   2xl → sm:max-w-2xl
 *
 * Body shape (from the design doc):
 *   <form id="edit-foo" className="space-y-6">
 *     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 *       <Input variant="flat" ... />
 *       <Input variant="flat" ... />
 *     </div>
 *     <div className="pt-2 border-t">
 *       <p className="text-xs font-medium text-muted-foreground mb-3">Sub group</p>
 *       ...
 *     </div>
 *   </form>
 *
 * Inputs / Textareas / SelectTriggers MUST use `variant="flat"` inside a drawer.
 *
 * The underlying Sheet is configured `modal={false}` so focus doesn't get
 * trapped — nested Combobox / Popover portals would otherwise dismiss the
 * drawer or freeze focus.
 */
export type SlideDrawerSize = "sm" | "md" | "lg" | "xl" | "2xl";

export interface SlideDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Right-aligned slot in the drawer header (e.g. an inline status chip) */
  headerRight?: React.ReactNode;
  size?: SlideDrawerSize;
  /** Footer rendered fixed at the bottom of the drawer */
  footer?: React.ReactNode;
  /**
   * When true, the drawer can be dismissed by clicking the overlay.
   * Defaults to true. FormDrawer flips this off so users don't lose form
   * state by accident.
   */
  dismissOnOverlayClick?: boolean;
  className?: string;
  contentClassName?: string;
  /** Stable test id appended to the outer SheetContent. */
  "data-testid"?: string;
  children: React.ReactNode;
}

export function SlideDrawer({
  open,
  onOpenChange,
  title,
  description,
  headerRight,
  size = "md",
  footer,
  dismissOnOverlayClick = true,
  className,
  contentClassName,
  "data-testid": dataTestId,
  children,
}: SlideDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        variant="floating"
        size={size}
        data-testid={dataTestId}
        onOverlayClick={dismissOnOverlayClick ? () => onOpenChange(false) : undefined}
        className={cn("p-0", className)}
        hideClose={false}
      >
        {/* Header band */}
        <SheetHeader className="flex flex-row items-start justify-between gap-3 border-b border-border/60 px-6 pb-4 pt-6 pr-12">
          <div className="flex-1 min-w-0">
            <SheetTitle className="text-base">{title}</SheetTitle>
            {description ? (
              <SheetDescription className="mt-1">
                {description}
              </SheetDescription>
            ) : null}
          </div>
          {headerRight ? (
            <div className="shrink-0">{headerRight}</div>
          ) : null}
        </SheetHeader>

        {/* Scrollable body */}
        <div className={cn("flex-1 overflow-y-auto px-6 py-4", contentClassName)}>
          {children}
        </div>

        {/* Optional sticky footer */}
        {footer ? (
          <div className="border-t border-border/60 px-6 py-4">{footer}</div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
